/**
 * Cloak overlay — frontend safety net.
 *
 * Last-mile DOM-side guard: even when the server-side overlay is
 * active, the CRM still displays the upstream organization / store
 * names (which often leak the real vertical, e.g. "The American Pep",
 * "Peptide Lab"). When the overlay is ON, this utility blanks out any
 * org/store name that contains a known pharma/peptide keyword.
 *
 * Scope is intentionally tiny — `name` strings only, called explicitly
 * at the 4 known render sites (sidebar trigger, sidebar list, org
 * dropdown trigger, org dropdown list). No DOM scanner / observer.
 *
 * Activation signal: a `cloakOverlayUntil` epoch ms in localStorage,
 * written by `CloakOverlayToggle` after a successful toggle. The
 * server is still the source of truth (the keyboard sequence ALSO
 * 404s for non-allowed users so they'll never set the flag). Auto-
 * expires with the timestamp; nothing else to clean up.
 *
 * Removable with the rest of the cloak module — see
 * `src/app/services/cloakingOverlay/README.md`.
 */

import * as React from 'react';

const STORAGE_KEY = 'tgd_cloak_until';
const PLACEHOLDER = '\u2022\u2022\u2022'; // "•••"

// Conservative — high-signal pharma / peptide / SARM keywords only.
// Word-boundary anchored to avoid trivial false positives. False
// positives here just mean a perfectly innocent org name gets blanked,
// which is the safe side of the bet for a demo-only feature.
const RISKY_PATTERNS: RegExp[] = [
  /\bpeptides?\b/i,
  /\bpep\b/i,
  /\bIGF([\s-]?\d+)?\b/i,
  /\bBPC([\s-]?\d+)?\b/i,
  /\bGHK([\s-]?CU)?\b/i,
  /\bTB[\s-]?\d+\b/i,
  /\bMOTS[\s-]?C\b/i,
  /\bipamorelin\b/i,
  /\bmelanotan\b/i,
  /\bsemaglutide\b/i,
  /\btirzepatide\b/i,
  /\boxytocin\b/i,
  /\bresearch\s*-?\s*compound[s]?\b/i,
  /\bSARMs?\b/i,
  /\bsteroids?\b/i,
  /\bHGH\b/i,
  /\bgrowth\s+hormone\b/i,
  /\bnootropics?\b/i,
];

const CHANGE_EVENT = 'cloak-active-changed';

/**
 * Persist (or clear) the activation flag. Called by the toggle
 * component on a successful toggle response; the `until` field comes
 * straight from the server. `null`/past timestamp ⇒ clear.
 */
export function setCloakUntil(until: number | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (typeof until === 'number' && until > Date.now()) {
      localStorage.setItem(STORAGE_KEY, String(until));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch {
    // localStorage disabled (private mode, etc.) — silently no-op.
  }
}

function isActiveSync(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/** React hook: subscribes to localStorage + same-tab change events. */
export function useCloakActive(): boolean {
  const [active, setActive] = React.useState<boolean>(() => isActiveSync());
  React.useEffect(() => {
    const update = () => setActive(isActiveSync());
    window.addEventListener('storage', update);
    window.addEventListener(CHANGE_EVENT, update);
    return () => {
      window.removeEventListener('storage', update);
      window.removeEventListener(CHANGE_EVENT, update);
    };
  }, []);
  return active;
}

/** Pure: same input ⇒ same output, no React. */
export function sanitizeCloakName(name: string | undefined | null, active: boolean): string {
  if (!active || !name) return name ?? '';
  for (const re of RISKY_PATTERNS) {
    if (re.test(name)) return PLACEHOLDER;
  }
  return name;
}

/** Convenience hook: returns a stable sanitizer bound to the current state. */
export function useCloakSanitizer(): (name: string | undefined | null) => string {
  const active = useCloakActive();
  return React.useCallback((name) => sanitizeCloakName(name, active), [active]);
}
